import { AxiosResponse } from "axios";
import { ITripType } from "../entities/DTO-models/ITripType";
import { mtbService } from "./mtbService";
import {
  DuplicateTripCodeError,
  ErrorCode,
  InSufficientTenantRights,
  InvalidTenantError,
  NotFoundError,
} from "../entities/Error/ErrorCodes";
import { IProgramType, ITripList_RM } from "../entities/DTO-models";

export class TripService {
  public async getProgramForTrip(
    tenantKey: string,
    tripCode: string,
    options?: { includeUnpublishedDays?: boolean }
  ): Promise<ITripType> {
    try {
      const url = `/tenant/${tenantKey}/trip/${tripCode}/program`;
      const params = options?.includeUnpublishedDays
        ? { includeUnpublishedDays: true }
        : undefined;
      const json: AxiosResponse = await mtbService.getProtected(url, params);
      const rawTrip = json.data;
      const tripInfo: ITripType = {
        ...rawTrip,
        start: new Date(rawTrip.start),
        end: new Date(rawTrip.end),
        published: Boolean(rawTrip.published),
        destinationId: rawTrip.destinationId
          ? Number(rawTrip.destinationId)
          : undefined,
        programs: rawTrip.programs
          ? rawTrip.programs.map((program: IProgramType) => ({
              ...program,
              id: program.id ? Number(program.id) : undefined,
              masterProgram: Boolean(program.masterProgram),
              isTemplate:
                program.isTemplate !== undefined
                  ? Boolean(program.isTemplate)
                  : undefined,
              programTemplateId: program.programTemplateId
                ? Number(program.programTemplateId)
                : undefined,
              trips: program.trips
                ? program.trips.map((trip) => ({
                    ...trip,
                    start: new Date(trip.start!),
                    end: new Date(trip.end!),
                    published: Boolean(trip.published),
                    destinationId: trip.destinationId
                      ? Number(trip.destinationId)
                      : undefined,
                  }))
                : undefined,
            }))
          : undefined,
      };
      return tripInfo;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.InvalidTripCode:
          throw new NotFoundError();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      throw errResponse;
    }
  }

  public async getTrip(tenantKey: string, tripId: string): Promise<ITripType> {
    try {
      const url = `/tenant/${tenantKey}/trip/${tripId}`;
      const json: AxiosResponse = await mtbService.getProtected(url);
      const rawTrip = json.data;
      const tripInfo: ITripType = {
        ...rawTrip,
        start: new Date(rawTrip.start),
        end: new Date(rawTrip.end),
        published: Boolean(rawTrip.published),
        destinationId: rawTrip.destinationId
          ? Number(rawTrip.destinationId)
          : undefined,
        destination: rawTrip.destination
          ? {
              ...rawTrip.destination,
              id: rawTrip.destination.id
                ? Number(rawTrip.destination.id)
                : undefined,
              area: rawTrip.destination.area
                ? {
                    areaId: Number(rawTrip.destination.area.areaId),
                    country: rawTrip.destination.area.country,
                    title: rawTrip.destination.area.title,
                  }
                : undefined,
              published: Boolean(rawTrip.destination.published),
            }
          : undefined,
        programs: rawTrip.programs
          ? rawTrip.programs.map((program: IProgramType) => ({
              ...program,
              id: program.id ? Number(program.id) : undefined,
              masterProgram: Boolean(program.masterProgram),
              isTemplate:
                program.isTemplate !== undefined
                  ? Boolean(program.isTemplate)
                  : undefined,
              programTemplateId: program.programTemplateId
                ? Number(program.programTemplateId)
                : undefined,
              trips: program.trips
                ? program.trips.map((trip: ITripType) => ({
                    ...trip,
                    start: new Date(trip.start!),
                    end: new Date(trip.end!),
                    published: Boolean(trip.published),
                    destinationId: trip.destinationId
                      ? Number(trip.destinationId)
                      : undefined,
                  }))
                : undefined,
            }))
          : undefined,
      };
      return tripInfo;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      throw errResponse;
    }
  }

  public async registerClientForTripNotifications(
    tenantKey: string,
    tripCode: string,
    clientToken: string,
    destinationId?: number,
    destinationAlias?: string | null,
    platform?: "web" | "ios" | "android",
  ): Promise<void> {
    try {
      const payload: Record<string, unknown> = {
        clientToken,
      };
      const normalizedDestinationId =
        destinationId !== undefined && destinationId !== null
          ? Number(destinationId)
          : NaN;
      if (Number.isFinite(normalizedDestinationId)) {
        payload.destinationId = normalizedDestinationId;
      }
      if (destinationAlias) {
        payload.destinationAlias = destinationAlias;
      }
      if (platform) {
        payload.platform = platform;
      }
      const data = JSON.stringify(payload);
      const url = `/tenant/${tenantKey}/trip/${tripCode}/subscribeForTripMessages`;
      await mtbService.postPutProtected("post", url, data);
    } catch (errResponse: any) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.InvalidTripCode:
          throw new NotFoundError();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      throw errResponse;
    }
  }

  public async unregisterClientForTripNotifications(
    tenantKey: string,
    tripCode: string,
    clientToken: string,
    destinationId?: number,
    destinationAlias?: string | null,
    platform?: "web" | "ios" | "android",
  ): Promise<void> {
    try {
      const payload: Record<string, unknown> = {
        clientToken,
      };
      const normalizedDestinationId =
        destinationId !== undefined && destinationId !== null
          ? Number(destinationId)
          : NaN;
      if (Number.isFinite(normalizedDestinationId)) {
        payload.destinationId = normalizedDestinationId;
      }
      if (destinationAlias) {
        payload.destinationAlias = destinationAlias;
      }
      if (platform) {
        payload.platform = platform;
      }
      const data = JSON.stringify(payload);
      const url = `/tenant/${tenantKey}/trip/${tripCode}/unsubscribeFromTripMessages`;
      await mtbService.postPutProtected("post", url, data);
    } catch (errResponse: any) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.InvalidTripCode:
          throw new NotFoundError();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      throw errResponse;
    }
  }

  public async getMyTrips(
    tenantKey: string,
    includePassedTrips?: boolean,
    fetchCount?: number,
    fetchOffset?: number
  ): Promise<ITripList_RM> {
    try {
      const url = `/tenant/${tenantKey}/trip`;
      const params = {
        view: "myTrips",
        includePassedTrips: includePassedTrips
          ? includePassedTrips.toString()
          : undefined,
        fetchCount: fetchCount ? fetchCount.toString() : undefined,
        fetchOffset: fetchOffset ? fetchOffset.toString() : undefined,
      };
      const json = await mtbService.getProtected(url, params);
      // Parse dates in the response
      const data = json.data;
      if (data.weeks) {
        data.weeks.forEach((week: any) => {
          week.weekStart = new Date(week.weekStart);
          if (week.countries) {
            week.countries.forEach((country: any) => {
              if (country.areas) {
                country.areas.forEach((area: any) => {
                  if (area.trips) {
                    area.trips.forEach((trip: any) => {
                      trip.start = new Date(trip.start);
                      trip.end = new Date(trip.end);
                    });
                  }
                });
              }
            });
          }
        });
      }
      return data;
    } catch (errResponse: any) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      throw errResponse;
    }
  }

  public async getAllTrips(
    tenantKey: string,
    includePassedTrips?: boolean,
    fetchCount?: number,
    fetchOffset?: number,
    from?: Date,
    to?: Date,
    destinationId?: number,
    areaId?: number
  ): Promise<ITripList_RM> {
    try {
      const url = `/tenant/${tenantKey}/trip`;
      const params = {
        view: "allTrips",
        includePassedTrips: includePassedTrips
          ? includePassedTrips.toString()
          : undefined,
        fetchCount: fetchCount ? fetchCount.toString() : undefined,
        fetchOffset: fetchOffset ? fetchOffset.toString() : undefined,
        from: from ? from.toISOString() : undefined,
        to: to ? to.toISOString() : undefined,
        destinationId: destinationId ? destinationId.toString() : undefined,
        areaId: areaId ? areaId.toString() : undefined,
      };
      const json = await mtbService.getProtected(url, params);
      // Parse dates in the response
      const data = json.data;
      if (data.weeks) {
        data.weeks.forEach((week: any) => {
          week.weekStart = new Date(week.weekStart);
          if (week.countries) {
            week.countries.forEach((country: any) => {
              if (country.areas) {
                country.areas.forEach((area: any) => {
                  if (area.trips) {
                    area.trips.forEach((trip: any) => {
                      trip.start = new Date(trip.start);
                      trip.end = new Date(trip.end);
                    });
                  }
                });
              }
            });
          }
        });
      }
      return data;
    } catch (errResponse: any) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        default:
          break;
      }
      throw errResponse;
    }
  }

  public async saveTrip(
    tenantKey: string,
    tripData: ITripType
  ): Promise<ITripType> {
    try {
      const url = `/tenant/${tenantKey}/trip`;
      const data = JSON.stringify(tripData);
      const json: AxiosResponse = await mtbService.postPutProtected(
        "post",
        url,
        data
      );
      return json.data;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        case ErrorCode.DuplicateTripCode:
          throw new DuplicateTripCodeError();

        default:
          break;
      }
      throw errResponse;
    }
  }

  public async updateTrip(
    tenantKey: string,
    tripCode: string,
    tripData: ITripType
  ): Promise<ITripType> {
    try {
      const url = `/tenant/${tenantKey}/trip/${tripCode}`;
      const data = JSON.stringify(tripData);
      const json: AxiosResponse = await mtbService.postPutProtected(
        "put",
        url,
        data
      );
      return json.data;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.NotFound:
          throw new NotFoundError();
        case ErrorCode.DuplicateTripCode:
          throw new DuplicateTripCodeError();

        default:
          break;
      }
      throw errResponse;
    }
  }

  public async deleteTrip(tenantKey: string, tripCode: string): Promise<void> {
    try {
      const url = `/tenant/${tenantKey}/trip/${tripCode}`;
      await mtbService.deleteProtected(url);
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.NotFound:
          throw new NotFoundError();

        default:
          break;
      }
      throw errResponse;
    }
  }

  public async getTourguides(tenantKey: string): Promise<any[]> {
    try {
      const url = `/tenant/${tenantKey}/tenantUser/tourguides`;
      const json = await mtbService.getProtected(url);
      return json.data;
    } catch (errResponse) {
      const errCode: ErrorCode = errResponse.response?.data
        .errorCode as ErrorCode;
      switch (errCode) {
        case ErrorCode.InsufficientRights:
          throw new InSufficientTenantRights();
        case ErrorCode.InvalidTenantError:
          throw new InvalidTenantError();
        case ErrorCode.NotFound:
          throw new NotFoundError();

        default:
          break;
      }
      throw errResponse;
    }
  }
}
